import { useState } from 'react';
import { useAuth } from './AuthContext';
import { useFirmConfig } from '../contexts/FirmConfigContext';
import { api } from '../api/http';
import NativeLoginForm from './NativeLoginForm';

type View = 'login' | 'forgot';

export default function LoginPage() {
  const { login, authMethods } = useAuth();
  const firm = useFirmConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const hasMicrosoft = authMethods.includes('microsoft');
  const hasNative = authMethods.includes('native');

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await login({ explicitReauth: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post('/auth/native/forgot-password', { identifier: forgotEmail });
    } catch {
      // Always show neutral confirmation — never reveal account existence
    } finally {
      setForgotLoading(false);
      setForgotSent(true);
    }
  };

  const microsoftButton = (
    <button
      onClick={handleLogin}
      disabled={loading}
      style={{
        width: '100%',
        padding: '0.75rem 1.5rem',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: '#fff',
        background: loading ? '#78716c' : 'var(--primary)',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        cursor: loading ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.6rem',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#e11d48'; }}
      onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = 'var(--primary)'; }}
    >
      {/* Microsoft logo */}
      <svg width="18" height="18" viewBox="0 0 21 21">
        <rect x="0" y="0" width="10" height="10" fill="#f25022" />
        <rect x="11" y="0" width="10" height="10" fill="#7fba00" />
        <rect x="0" y="11" width="10" height="10" fill="#00a4ef" />
        <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
      </svg>
      {loading ? 'Signing in...' : 'Sign in with Microsoft'}
    </button>
  );

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1023 0%, #2d1b3d 40%, #4a2040 70%, #6b2040 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{
        background: 'var(--card)',
        borderRadius: '16px',
        padding: '3rem 2.5rem',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.25)',
        textAlign: 'center',
      }}>
        {/* Valentine branding */}
        <img
          src="/favicon-96x96.png"
          alt="Valentine"
          width={56}
          height={56}
          style={{ margin: '0 auto 1.25rem', display: 'block' }}
        />

        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--text)',
          margin: '0 0 0.25rem',
        }}>
          Valentine
        </h1>
        <p style={{
          fontSize: '0.875rem',
          color: 'var(--text-muted)',
          margin: '0 0 2rem',
        }}>
          Case Management System
        </p>

        {/* Forgot-password inline view */}
        {view === 'forgot' && (
          forgotSent ? (
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
                If an account exists for that email, we've sent a password reset link.
              </p>
              <button
                type="button"
                onClick={() => { setView('login'); setForgotSent(false); setForgotEmail(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.875rem',
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                ← Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgot} noValidate style={{ textAlign: 'left' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Enter your email and we'll send a reset link.
              </p>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.35rem' }} htmlFor="forgot-email">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="username email"
                  required
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    fontSize: '0.9rem',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--input, #fff)',
                    color: 'var(--text)',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  placeholder="you@firm.com"
                />
              </div>
              <button
                type="submit"
                disabled={forgotLoading}
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#fff',
                  background: forgotLoading ? '#78716c' : 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: forgotLoading ? 'not-allowed' : 'pointer',
                  marginBottom: '0.75rem',
                }}
              >
                {forgotLoading ? 'Sending...' : 'Send reset link'}
              </button>
              <button
                type="button"
                onClick={() => setView('login')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.8rem',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                ← Back to sign in
              </button>
            </form>
          )
        )}

        {/* Native only */}
        {view === 'login' && hasNative && !hasMicrosoft && (
          <NativeLoginForm onForgotPassword={() => setView('forgot')} />
        )}

        {/* Both methods */}
        {view === 'login' && hasNative && hasMicrosoft && (
          <>
            <NativeLoginForm onForgotPassword={() => setView('forgot')} />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              margin: '1.5rem 0',
            }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>or</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            {microsoftButton}
            {error && (
              <p style={{
                marginTop: '1rem',
                fontSize: '0.82rem',
                color: 'var(--danger)',
              }}>
                {error}
              </p>
            )}
          </>
        )}

        {/* Microsoft only (original behavior) */}
        {view === 'login' && hasMicrosoft && !hasNative && (
          <>
            {microsoftButton}
            {error && (
              <p style={{
                marginTop: '1rem',
                fontSize: '0.82rem',
                color: 'var(--danger)',
              }}>
                {error}
              </p>
            )}
          </>
        )}

        <p style={{
          marginTop: '2rem',
          fontSize: '0.75rem',
          color: 'var(--text-muted)',
        }}>
          {firm.name}
        </p>
      </div>
    </div>
  );
}
